import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { useEticketingContentsQuery, useEticketingCreateContentMutation, useEticketingUpdateContentMutation } from '../../../../api/e-ticketing/e-ticketing.hooks';
import { emojis } from '../../../components/bootstrap/alertData';
import ThemeEditor from './_components/ThemeEditor';
import NavbarEditor from './_components/NavbarEditor';
import HeroEditor from './_components/HeroEditor';
import AboutEditor from './_components/AboutEditor';
import ServicesEditor from './_components/ServicesEditor';
import FAQEditor from './_components/FAQEditor';
import ReviewsEditor from './_components/ReviewsEditor';
import FooterEditor from './_components/FooterEditor';
import MediaGalleryEditor from './_components/MediaGalleryEditor';

const Content = () => {
  const { serviceId } = useParams();
  const { data, isLoading, error, refetch } = useEticketingContentsQuery({ service_id: serviceId });

  const { create, isLoading: creating } = useEticketingCreateContentMutation();
  const { update, isLoading: updating } = useEticketingUpdateContentMutation();
  
  const [contentData, setContentData] = useState(null);
  const [contentId, setContentId] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (data?.content) {
      try {
        // Check if content is already an object or if it needs parsing
        const parsed = typeof data.content === 'string' ? JSON.parse(data.content) : data.content;
        setContentData(parsed);
        setContentId(data.id || data);
      } catch (err) {
        setErrorMsg('Failed to parse content data');
        console.error('Parse error:', err);
      }
    }
  }, [data]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        {emojis.warning} Failed to load content
      </Alert>
    );
  }

  if (!contentData) {
    return null;
  }

  const getSectionByType = (type) => {
    return contentData.sections?.find(s => s.id === type);
  };

  const updateSection = (type, updatedSection) => {
    const sectionExists = contentData.sections?.some(s => s.id === type);
    
    let newSections;
    if (sectionExists) {
      // Update existing section
      newSections = contentData.sections.map(s => 
        s.id === type ? updatedSection : s
      );
    } else {
      // Add new section if it doesn't exist
      newSections = [...(contentData.sections || []), updatedSection];
    }
    
    setContentData({ ...contentData, sections: newSections });
  };

  const handleSave = async () => {
    try {
      const contentPayload = JSON.stringify(contentData);
      
      if (contentId) {
        // Update existing content
        await update(contentId, {
          service_id: serviceId,
          content: contentPayload,
        });
        setSuccessMsg('Content updated successfully!');
      } else {
        await create({
          service_id: serviceId,
          content: contentPayload
        });
        setSuccessMsg('Content created successfully!');
      }
      
      refetch();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setErrorMsg(err?.message || 'Failed to save content');
      setTimeout(() => setErrorMsg(null), 3000);
    }
  };

  return (
    <div className="col-12">
      {successMsg && (
        <Alert variant="success" dismissible onClose={() => setSuccessMsg(null)}>
          {emojis.success} {successMsg}
        </Alert>
      )}
      {errorMsg && (
        <Alert variant="danger" dismissible onClose={() => setErrorMsg(null)}>
          {emojis.warning} {errorMsg}
        </Alert>
      )}

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title m-0">Content Management - Service: {data?.name || serviceId}</h4>
        </div>
        <div className="card-body">
          {/* Theme Section */}
          <ThemeEditor
            theme={contentData.theme}
            onChange={(theme) => setContentData({ ...contentData, theme })}
          />

          {/* Navbar Section */}
          <NavbarEditor
            navbar={getSectionByType('navbar')}
            onChange={(navbar) => updateSection('navbar', navbar)}
          />

          {/* Hero Section */}
          <HeroEditor
            hero={getSectionByType('hero')}
            onChange={(hero) => updateSection('hero', hero)}
          />

          {/* About Us Section */}
          <AboutEditor
            about={getSectionByType('ABOUT US')}
            onChange={(about) => updateSection('ABOUT US', about)}
          />

          {/* Services Section */}
          <ServicesEditor
            servicesSection={getSectionByType('OUR SERVICES')}
            onChange={(services) => updateSection('OUR SERVICES', services)}
          />

          {/* Media Gallery Section */}
          <MediaGalleryEditor
            mediaGallery={getSectionByType('MEDIA GALLERY')}
            onChange={(mediaGallery) => updateSection('MEDIA GALLERY', mediaGallery)}
          />

          {/* FAQs Section */}
          <FAQEditor
            faqSection={getSectionByType('faqs')}
            onChange={(faqs) => updateSection('faqs', faqs)}
          />

          {/* Reviews Section */}
          <ReviewsEditor
            reviewsSection={getSectionByType('reviews')}
            onChange={(reviews) => updateSection('reviews', reviews)}
          />

          {/* Footer Section */}
          <FooterEditor
            footer={getSectionByType('footer')}
            onChange={(footer) => updateSection('footer', footer)}
          />

          {/* Save Button at Bottom */}
          <div className="d-flex justify-content-end mt-4">
            <Button variant="primary" onClick={handleSave} disabled={creating || updating}>
              {creating || updating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {contentId ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <i className="fa fa-save me-1"></i> {contentId ? 'Update Content' : 'Create Content'}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Content;